import { useEffect, useState } from "react";
import { API_BASE, till } from "@/lib/api";
import { displayLink, listenAsDisplay, offerCodeToOpener, replyFromDisplay, saveDisplayLink, type DisplayMessage, type PayView } from "@/lib/display";
import { money } from "@/lib/format";
import { PHONE_HINT, PROVIDERS, fmtPhone, ghPhone, guessProvider, providerLabel, type Provider } from "@/lib/paystack";
import { PayLinkFlow } from "@/components/PayLinkFlow";
import { Brand } from "@/components/Brand";
import { Qr } from "@/components/Qr";
import { DisplayShop } from "@/components/DisplayShop";
import { cx } from "@/components/ui";
import { useInstall } from "./lib/install";

const ICON = {
  momo: "M7 2h10v20H7zM11 18h2",
  card: "M2 6h20v12H2zM2 10h20M6 15h4",
  cash: "M2 7h20v10H2zM12 12h.01M6 12h.01M18 12h.01",
};

/**
 * Customer-facing screen, on its own site: a tablet or screen on the counter, or a second window
 * opened by the till (linked once with a code, then stays linked). Shows the items, then just
 * what the customer needs while paying: tap a method, type their MoMo number, "approve on your
 * phone", a QR, or their change.
 */
export function Display() {
  const [linked, setLinked] = useState(() => !!displayLink());
  if (!linked) return <PairDisplay onLinked={() => setLinked(true)} />;
  return <Screen onUnlinked={() => setLinked(false)} />;
}

function Screen({ onUnlinked }: { onUnlinked: () => void }) {
  const [msg, setMsg] = useState<DisplayMessage>({ type: "idle" });
  const [lastCart, setLastCart] = useState<Extract<DisplayMessage, { type: "cart" }> | null>(null);
  useEffect(
    () =>
      listenAsDisplay((m) => {
        setMsg(m);
        if (m.type === "cart") setLastCart(m);
        if (m.type === "idle") setLastCart(null);
      }, onUnlinked),
    [onUnlinked],
  );
  useEffect(() => {
    if (msg.type !== "paid") return;
    const t = setTimeout(() => setMsg({ type: "idle" }), 30_000);
    return () => clearTimeout(t);
  }, [msg]);

  // No sale being rung up: customers can browse and order (when the shop allows it).
  if (msg.type === "idle") return <DisplayShop />;
  if (msg.type === "paid") return <Paid msg={msg} />;

  const cart = msg.type === "cart" ? msg : lastCart;
  const paying = msg.type === "pay" ? msg.view : null;
  return (
    <div className="flex min-h-full flex-col bg-ink text-white lg:h-full lg:flex-row">
      <section className={cx("flex min-h-0 flex-1 flex-col gap-4 p-5 sm:p-8", paying && "hidden lg:flex")}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Brand sub="Your items" />
          {msg.customer ? <span className="text-base text-white/65">Welcome back, <b className="text-white">{msg.customer}</b></span> : null}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {(cart?.lines ?? []).map((l, i) => (
            <div key={i} className="flex justify-between gap-6 border-b border-steel-2 py-3 text-base sm:text-lg">
              <span className="font-semibold">{l.qty} × {l.name}</span>
              <span className="font-mono">{money(l.total)}</span>
            </div>
          ))}
        </div>
        {cart ? (
          <div className="flex flex-wrap items-baseline justify-between gap-4 bg-brand px-5 py-4">
            <span className="flex flex-col">
              {cart.savings ? <span className="text-base">You save {money(cart.savings)}</span> : null}
              <span className="font-mono text-sm tracking-widest">TOTAL</span>
            </span>
            <span className="font-mono text-4xl font-semibold sm:text-5xl">{money(cart.total)}</span>
          </div>
        ) : null}
      </section>
      {paying ? (
        <aside className="flex w-full flex-1 flex-col gap-4 bg-graphite p-5 sm:p-8 lg:w-[460px] lg:flex-none">
          <PayPanel view={paying} />
        </aside>
      ) : null}
    </div>
  );
}

function PayPanel({ view }: { view: PayView }) {
  switch (view.stage) {
    case "choose":
      return (
        <>
          <Title eyebrow={`TO PAY ${money(view.left)}`} title="How would you like to pay?" sub="Tap one, or just tell the cashier." />
          <div className="flex flex-col gap-2.5">
            <Choice icon={ICON.momo} title="Mobile Money" sub="MTN, Telecel, AirtelTigo" onClick={() => replyFromDisplay({ type: "pick-method", method: "momo" })} />
            <Choice icon={ICON.card} title="Card" sub="Pay on your phone with a QR" onClick={() => replyFromDisplay({ type: "pick-method", method: "card" })} />
            <Choice icon={ICON.cash} title="Cash" sub="Your change shows here" onClick={() => replyFromDisplay({ type: "pick-method", method: "cash" })} />
          </div>
        </>
      );
    case "number":
      return <NumberEntry left={view.left} />;
    case "otp":
      return <CodeEntry message={view.message} />;
    case "prompt":
      return (
        <>
          <Title eyebrow={`MOBILE MONEY · ${money(view.left)}`} title="Check your phone" />
          <p className="text-xl leading-relaxed text-white/80">
            Approve the payment on <b className="font-mono text-white">{view.phone ?? "your phone"}</b>{view.provider ? ` (${providerLabel(view.provider)})` : ""} by entering your MoMo PIN.
          </p>
          <p className="text-base text-white/60">Never tell anyone your PIN, including the cashier.</p>
          <Waiting text="Waiting for you to approve" />
          <Back label="Wrong number? Change it" />
        </>
      );
    case "qr":
      return <QrOrHere view={view} />;
    case "link":
      return (
        <>
          <Title eyebrow={`PAYMENT LINK · ${money(view.left)}`} title={`Link sent to ${view.phone}`} />
          <p className="text-xl leading-relaxed text-white/80">Open the SMS and tap the link to pay. This screen updates as soon as it's paid.</p>
          <Waiting text="Waiting for your payment" />
          <Back />
        </>
      );
    case "cash":
      return <Title eyebrow={`CASH · ${money(view.left)}`} title="Hand your cash to the cashier" sub="Your change shows here once it's counted." />;
    case "declined":
      return (
        <>
          <Title eyebrow="NOT PAID" title="That payment didn't go through" sub={view.message || "Nothing was taken. Try again, or choose another way."} />
          <Back label="Choose again" />
        </>
      );
  }
}

function Title({ eyebrow, title, sub }: { eyebrow?: string; title: string; sub?: string }) {
  return (
    <div className="flex flex-col gap-2">
      {eyebrow ? <span className="font-mono text-[13px] tracking-wider text-brand-light">{eyebrow}</span> : null}
      <h1 className="text-2xl font-black leading-tight sm:text-3xl">{title}</h1>
      {sub ? <p className="text-base text-white/70">{sub}</p> : null}
    </div>
  );
}
function Choice({ title, sub, onClick, icon }: { title: string; sub: string; onClick: () => void; icon: string }) {
  return (
    <button type="button" onClick={onClick} className="flex min-h-[72px] items-center gap-3.5 border-2 border-steel bg-steel px-4 py-2.5 text-left text-white hover:border-brand-light">
      <span className="grid h-11 w-11 flex-none place-items-center bg-brand">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d={icon} /></svg>
      </span>
      <span className="flex flex-col gap-0.5">
        <b className="text-lg">{title}</b>
        <span className="text-[13px] text-white/65">{sub}</span>
      </span>
    </button>
  );
}
function Waiting({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3 bg-ink px-4 py-4 text-lg">
      <span className="h-3 w-3 flex-none animate-pulse bg-warn" />
      {text}
    </div>
  );
}
function Back({ label = "Choose another way" }: { label?: string }) {
  return (
    <button type="button" onClick={() => replyFromDisplay({ type: "back" })} className="self-start py-2 text-[17px] font-bold text-brand-light underline underline-offset-4">
      {label}
    </button>
  );
}
function Pad({ onKey }: { onKey: (k: string) => void }) {
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "⌫"].map((k) => (
        <button key={k} type="button" onClick={() => onKey(k)} aria-label={k === "⌫" ? "Delete" : k === "C" ? "Clear" : k} className="h-16 bg-steel font-mono text-2xl font-semibold text-white hover:bg-steel-2">
          {k}
        </button>
      ))}
    </div>
  );
}

/** Pay by scanning with their own phone, or type the details right here on Paystack's secure form. */
function QrOrHere({ view }: { view: Extract<PayView, { stage: "qr" }> }) {
  const [here, setHere] = useState(false);
  const card = view.method === "card";
  if (here && view.code)
    return (
      <>
        <Title eyebrow={`${card ? "CARD" : "MOBILE MONEY"} · ${money(view.left)}`} title="Pay on this screen" sub="The cashier can't see what you type." />
        <div className="max-h-[70vh] overflow-y-auto">
          <PayLinkFlow code={view.code} embedded />
        </div>
        <button type="button" onClick={() => setHere(false)} className="self-start py-2 text-[17px] font-bold text-brand-light underline underline-offset-4">Scan a QR instead</button>
      </>
    );
  return (
    <>
      <Title eyebrow={`${card ? "CARD" : "MOBILE MONEY"} · ${money(view.left)}`} title={card ? "Pay by card" : "Pay with Mobile Money"} sub="Two ways. Pick whichever is easier." />
      <div className="flex flex-col gap-3 border-2 border-steel p-4">
        <span className="font-mono text-[12px] tracking-wider text-brand-light">1 · SCAN WITH YOUR PHONE</span>
        <div className="flex flex-wrap items-center gap-5">
          <div className="bg-white p-2.5">
            <Qr text={view.url} size={200} label="Payment QR code" />
          </div>
          <p className="min-w-[160px] flex-1 text-base leading-relaxed text-white/80">Point your camera here. Our secure payment page opens with the amount filled in.</p>
        </div>
      </div>
      {view.code ? (
        <div className="flex flex-col gap-3 border-2 border-steel p-4">
          <span className="font-mono text-[12px] tracking-wider text-brand-light">2 · OR PAY ON THIS SCREEN</span>
          <p className="text-base text-white/75">{card ? "Your card goes into Paystack's secure window." : "Type your number and approve on your phone."} The cashier can't see what you type.</p>
          <button type="button" onClick={() => setHere(true)} className="h-16 bg-brand text-lg font-extrabold text-white">
            {card ? "Enter my card details" : "Enter my number"}
          </button>
        </div>
      ) : null}
      <Waiting text="Waiting for your payment. It confirms by itself." />
      <Back />
    </>
  );
}

function NumberEntry({ left }: { left: number }) {
  const [raw, setNum] = useState("");
  const [net, setNet] = useState<Provider | null>(null);
  const ph = ghPhone(raw);
  const num = ph.local;
  const shownNet = net ?? (ph.ok ? guessProvider(num) : null);
  const key = (k: string) => setNum((v) => (k === "C" ? "" : k === "⌫" ? v.slice(0, -1) : ghPhone(v + k).keep));
  return (
    <>
      <Title eyebrow={`MOBILE MONEY · ${money(left)}`} title="Your Mobile Money number" sub="We'll send a payment request to this phone." />
      <div aria-live="polite" className="flex h-[72px] items-center justify-between border-2 border-brand-light bg-ink px-4">
        <span className="font-mono text-3xl font-semibold tracking-wide">{raw ? (ph.ok ? fmtPhone(num) : raw) : <span className="text-white/35">0__ ___ ____</span>}</span>
        <span className="text-base font-bold text-brand-light">{shownNet ? providerLabel(shownNet) : ""}</span>
      </div>
      {raw && !ph.ok ? <span className="text-sm text-[#ffb36b]">{PHONE_HINT}</span> : null}
      <Pad onKey={key} />
      {ph.ok && !guessProvider(num) ? (
        <div role="radiogroup" aria-label="Network" className="grid grid-cols-3 gap-1.5">
          {PROVIDERS.map((p) => (
            <button key={p.id} type="button" role="radio" aria-checked={shownNet === p.id} onClick={() => setNet(p.id)} className={cx("h-12 border-2 font-bold", shownNet === p.id ? "border-brand-light bg-white text-ink" : "border-steel bg-steel")}>
              {p.label}
            </button>
          ))}
        </div>
      ) : null}
      <button type="button" disabled={!ph.ok || !shownNet} onClick={() => shownNet && replyFromDisplay({ type: "momo-number", phone: num, provider: shownNet })} className="h-[72px] bg-brand text-xl font-extrabold text-white disabled:bg-steel-2 disabled:text-white/45">
        Send me the payment request
      </button>
      <button type="button" onClick={() => replyFromDisplay({ type: "scan", method: "momo" })} className="h-14 border-2 border-steel text-lg font-bold text-white hover:border-brand-light">
        Or scan a QR to pay on my phone
      </button>
      <Back />
    </>
  );
}

function CodeEntry({ message }: { message: string | null }) {
  const [code, setCode] = useState("");
  const key = (k: string) => setCode((v) => (k === "C" ? "" : k === "⌫" ? v.slice(0, -1) : (v + k).slice(0, 8)));
  return (
    <>
      <Title eyebrow="ONE-TIME CODE" title="Type the code you received" sub={`${message || "A code was sent to your phone by SMS."} Only type it here.`} />
      <div aria-live="polite" className="flex h-[72px] items-center border-2 border-brand-light bg-ink px-4 font-mono text-3xl font-semibold tracking-[0.3em]">{code || <span className="text-white/35">______</span>}</div>
      <Pad onKey={key} />
      <button type="button" disabled={code.length < 4} onClick={() => replyFromDisplay({ type: "otp", otp: code })} className="h-[72px] bg-brand text-xl font-extrabold text-white disabled:bg-steel-2 disabled:text-white/45">
        Confirm code
      </button>
    </>
  );
}

function Paid({ msg }: { msg: Extract<DisplayMessage, { type: "paid" }> }) {
  const [receipt, setReceipt] = useState<string | null>(null);
  const [stars, setStars] = useState(0);
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-7 bg-ink px-6 py-10 text-center text-white">
      <h1 className="text-4xl font-black text-brand-light sm:text-5xl">Thank you{msg.customer ? `, ${msg.customer.split(" ")[0]}` : ""}!</h1>
      <p className="font-mono text-xl sm:text-2xl">Paid {money(msg.total)}{msg.paidWith ? ` · ${msg.paidWith}` : ""}</p>
      {msg.change ? <p className="bg-brand px-6 py-4 font-mono text-3xl font-semibold">Your change: {money(msg.change)}</p> : null}
      {msg.warranty ? <p className="text-lg text-white/70">Warranty registered: {msg.warranty}</p> : null}
      <div className="flex w-full max-w-lg flex-col gap-2.5">
        <span className="text-lg font-bold">Your receipt</span>
        <div className="grid grid-cols-3 gap-2">
          {(["sms", "whatsapp", "paper"] as const).map((c) => (
            <button key={c} type="button" onClick={() => { setReceipt(c); replyFromDisplay({ type: "receipt", channel: c }); }} className={cx("h-16 text-lg font-bold", receipt === c ? "border-[3px] border-brand-light bg-white text-ink" : "bg-steel")}>
              {c === "sms" ? "SMS" : c === "whatsapp" ? "WhatsApp" : "Paper"}
              {receipt === c ? " ✓" : ""}
            </button>
          ))}
        </div>
      </div>
      <div className="flex w-full max-w-lg flex-col gap-2.5">
        <span className="text-lg font-bold">How was our service today?</span>
        <div className="grid grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" aria-label={`${n} out of 5`} onClick={() => { setStars(n); replyFromDisplay({ type: "rating", stars: n }); }} className={cx("h-16 text-3xl", n <= stars ? "bg-brand text-[#ffd84d]" : "bg-steel text-[#ffd84d]/70")}>
              ★
            </button>
          ))}
        </div>
        {stars ? <span className="text-white/70">Thanks for telling us.</span> : null}
      </div>
    </div>
  );
}

/** A new tablet / phone: show a code for the cashier to type on the till. */
function PairDisplay({ onLinked }: { onLinked: () => void }) {
  const app = useInstall();
  const [p, setP] = useState<{ code: string; displayId: string; secret: string; expires: number } | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    let timer = 0;
    const start = async () => {
      try {
        const r = await fetch(API_BASE + till("/display/start"), { method: "POST", credentials: "omit" });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Couldn't get a code");
        if (alive) {
          setErr("");
          setP(d);
          offerCodeToOpener(d.code);
          poll(d);
        }
      } catch (e) {
        if (alive) {
          setErr(e instanceof Error ? e.message : "No internet");
          timer = window.setTimeout(start, 5000);
        }
      }
    };
    const poll = (d: { displayId: string; secret: string; expires: number }) => {
      timer = window.setTimeout(async () => {
        if (!alive) return;
        if (Date.now() > d.expires - 5000) return void start();
        try {
          const r = await fetch(API_BASE + till("/display/claim"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ displayId: d.displayId, secret: d.secret }), credentials: "omit" });
          const j = await r.json();
          if (j.token) {
            saveDisplayLink({ token: j.token, till: j.till ?? null });
            onLinked();
            return;
          }
          if (r.status === 404) return void start();
        } catch {
          /* keep trying */
        }
        poll(d);
      }, 2000);
    };
    void start();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [onLinked]);
  return (
    <div className="flex h-full flex-col items-center justify-center gap-8 bg-ink px-6 text-center text-white">
      <Brand big sub="Customer display" />
      <div className="flex flex-col gap-3">
        <span className="text-xl text-white/70">On the till, tap <b className="text-white">Display</b> and type this code</span>
        <span className="font-mono text-7xl font-semibold tracking-[0.25em] text-brand-light sm:text-8xl">{p ? `${p.code.slice(0, 3)} ${p.code.slice(3)}` : "··· ···"}</span>
        {err ? <span className="text-[#ff9d7a]">{err}</span> : <span className="text-white/50">Waiting for the till…</span>}
      </div>
      {app.canPrompt ? (
        <button type="button" onClick={() => void app.install()} className="h-12 border border-white/30 px-5 font-semibold text-white/85 hover:border-white">
          Install the display as an app
        </button>
      ) : app.apple ? (
        <span className="text-sm text-white/50">To install: tap Share, then Add to Home Screen.</span>
      ) : null}
    </div>
  );
}
